import {
  FileText,
  ShieldCheck,
  AlertTriangle,
  Send,
  BarChart3,
  PhoneCall,
  Link2,
  ScanLine,
  Radar,
  Trophy,
  Wallet,
  Crosshair,
  Handshake,
  MapPin,
  TrendingUp,
  FileLock2,
  Server,
  GraduationCap,
  Eye,
} from "lucide-react";

// Navigation — stable paths, bilingual labels
export const NAV_LINKS = [
  { path: "/services", label: { en: "Services", es: "Servicios" } },
  { path: "/how-it-works", label: { en: "How It Works", es: "Cómo Funciona" } },
  { path: "/why-medprime", label: { en: "Why Us", es: "Por Qué Nosotros" } },
  { path: "/about", label: { en: "About", es: "Nosotros" } },
  { path: "/contact", label: { en: "Contact", es: "Contacto" } },
];

export const STATS = [
  {
    value: 98,
    suffix: { en: "%", es: "%" },
    label: { en: "Clean Claim Rate", es: "Reclamaciones Limpias" },
  },
  {
    value: 30,
    suffix: { en: "-Day", es: " Días" },
    label: { en: "Average Collection", es: "Cobro Promedio" },
  },
  {
    value: 35,
    suffix: { en: "%", es: "%" },
    label: { en: "Revenue Increase", es: "Aumento de Ingresos" },
  },
];

export const MARQUEE_ITEMS = {
  en: [
    "Medical Billing",
    "Claims Management",
    "Insurance Verification",
    "Denial Management",
    "HIPAA Compliant",
    "Puerto Rico Based",
    "Bilingual Service",
  ],
  es: [
    "Facturación Médica",
    "Gestión de Reclamaciones",
    "Verificación de Seguros",
    "Gestión de Denegaciones",
    "Cumplimiento HIPAA",
    "Basado en Puerto Rico",
    "Servicio Bilingüe",
  ],
};

export const SERVICES = [
  {
    icon: FileText,
    title: { en: "Medical Billing & Coding", es: "Facturación y Codificación Médica" },
    short: {
      en: "Accurate ICD-10 and CPT coding that captures every dollar your clinic has earned.",
      es: "Codificación precisa de ICD-10 y CPT que captura cada dólar que su clínica ha ganado.",
    },
    detail: {
      en: "Our certified coders review each encounter, apply correct ICD-10 and CPT codes, and submit clean claims the first time. Fewer rejections, faster payments, and full documentation you can audit anytime.",
      es: "Nuestros codificadores certificados revisan cada encuentro, aplican los códigos ICD-10 y CPT correctos y envían reclamaciones limpias desde el primer intento. Menos rechazos, pagos más rápidos y documentación completa que puede auditar en cualquier momento.",
    },
  },
  {
    icon: ShieldCheck,
    title: { en: "Insurance Verification", es: "Verificación de Seguros" },
    short: {
      en: "Real-time eligibility checks so you never deliver care you won't be paid for.",
      es: "Verificación de elegibilidad en tiempo real para que nunca brinde atención que no le será pagada.",
    },
    detail: {
      en: "We verify coverage, co-pays, deductibles, and authorizations before the patient walks in. Your front desk gets clarity, your patients get transparency, and your revenue stays protected.",
      es: "Verificamos cobertura, copagos, deducibles y autorizaciones antes de que el paciente llegue. Su recepción gana claridad, sus pacientes ganan transparencia y sus ingresos quedan protegidos.",
    },
  },
  {
    icon: AlertTriangle,
    title: { en: "Denial Management", es: "Gestión de Denegaciones" },
    short: {
      en: "We fight every denial with appeals built on evidence, not guesswork.",
      es: "Combatimos cada denegación con apelaciones basadas en evidencia, no en suposiciones.",
    },
    detail: {
      en: "Denied claims are tracked, root-caused, and appealed aggressively. We recover revenue payers hoped you would write off, and we fix the upstream issues so denials stop repeating.",
      es: "Las reclamaciones denegadas se rastrean, se analizan a fondo y se apelan con firmeza. Recuperamos ingresos que las aseguradoras esperaban que usted abandonara, y corregimos las causas de raíz para que las denegaciones dejen de repetirse.",
    },
  },
  {
    icon: Send,
    title: { en: "Claims Submission", es: "Envío de Reclamaciones" },
    short: {
      en: "Electronic submission and live tracking from clearinghouse to payment.",
      es: "Envío electrónico y seguimiento en vivo, desde la cámara de compensación hasta el pago.",
    },
    detail: {
      en: "Claims are scrubbed and submitted electronically with status tracking at every stage. You always know where each claim sits, and nothing falls through the cracks.",
      es: "Las reclamaciones se depuran y se envían electrónicamente con seguimiento de estado en cada etapa. Usted siempre sabe en qué punto está cada reclamación, y nada se queda atrás.",
    },
  },
  {
    icon: BarChart3,
    title: { en: "Revenue Reporting", es: "Informes de Ingresos" },
    short: {
      en: "Clear monthly dashboards showing exactly where your money is.",
      es: "Paneles mensuales claros que muestran exactamente dónde está su dinero.",
    },
    detail: {
      en: "Transparent reports on collections, A/R aging, payer mix, and KPIs, written for clinic owners, not accountants. Make confident decisions backed by real numbers.",
      es: "Informes transparentes sobre cobros, antigüedad de cuentas por cobrar, mezcla de aseguradoras y métricas clave, redactados para dueños de clínicas, no para contadores. Tome decisiones con confianza, respaldado por cifras reales.",
    },
  },
  {
    icon: PhoneCall,
    title: { en: "Patient Follow-Up Calls", es: "Llamadas de Seguimiento al Paciente" },
    short: {
      en: "Bilingual, respectful patient outreach that protects your reputation.",
      es: "Gestión bilingüe y respetuosa de pacientes que protege su reputación.",
    },
    detail: {
      en: "Our bilingual team handles statements, payment plans, and balance follow-ups with the courtesy your patients deserve, improving collections without straining relationships.",
      es: "Nuestro equipo bilingüe maneja estados de cuenta, planes de pago y seguimiento de balances con la cortesía que sus pacientes merecen, mejorando los cobros sin afectar las relaciones.",
    },
  },
];

export const STEPS = [
  {
    icon: Link2,
    title: { en: "We Connect to Your System", es: "Nos Conectamos a Su Sistema" },
    desc: {
      en: "We integrate securely with your EHR and practice management software in days, not months, with zero disruption to your team.",
      es: "Nos integramos de forma segura con su EHR y su sistema de gestión de práctica en días, no en meses, sin interrumpir a su equipo.",
    },
  },
  {
    icon: ScanLine,
    title: { en: "Claims Prepared & Scrubbed", es: "Reclamaciones Preparadas y Depuradas" },
    desc: {
      en: "Every claim is coded, reviewed, and scrubbed against payer rules before it ever leaves our hands, so it lands clean.",
      es: "Cada reclamación se codifica, revisa y depura contra las reglas de las aseguradoras antes de salir de nuestras manos, para que llegue limpia.",
    },
  },
  {
    icon: Radar,
    title: { en: "Electronic Submission & Tracking", es: "Envío Electrónico y Seguimiento" },
    desc: {
      en: "Claims are submitted electronically and tracked in real time from clearinghouse to remittance. Nothing gets lost.",
      es: "Las reclamaciones se envían electrónicamente y se rastrean en tiempo real, desde la cámara de compensación hasta la remesa. Nada se pierde.",
    },
  },
  {
    icon: Trophy,
    title: { en: "Denials Fought & Won", es: "Denegaciones Combatidas y Ganadas" },
    desc: {
      en: "When payers push back, we push harder, building evidence-based appeals that recover the revenue you earned.",
      es: "Cuando las aseguradoras se resisten, nosotros insistimos más, construyendo apelaciones basadas en evidencia que recuperan los ingresos que usted ganó.",
    },
  },
  {
    icon: Wallet,
    title: { en: "You Get Paid & Get Reports", es: "Usted Cobra y Recibe Informes" },
    desc: {
      en: "Money lands faster and you receive clear monthly reports showing exactly how your revenue is performing.",
      es: "El dinero llega más rápido y usted recibe informes mensuales claros que muestran exactamente cómo se desempeñan sus ingresos.",
    },
  },
];

export const VALUES = [
  {
    icon: Crosshair,
    title: { en: "Precision", es: "Precisión" },
    desc: {
      en: "Every code, every claim, every dollar accounted for.",
      es: "Cada código, cada reclamación, cada dólar contabilizado.",
    },
  },
  {
    icon: Handshake,
    title: { en: "Trust", es: "Confianza" },
    desc: {
      en: "Transparent reporting and HIPAA-grade security, always.",
      es: "Informes transparentes y seguridad de grado HIPAA, siempre.",
    },
  },
  {
    icon: MapPin,
    title: { en: "Local", es: "Local" },
    desc: {
      en: "Puerto Rico based, bilingual, and beside you on the island.",
      es: "Basados en Puerto Rico, bilingües y a su lado en la isla.",
    },
  },
  {
    icon: TrendingUp,
    title: { en: "Growth", es: "Crecimiento" },
    desc: {
      en: "We measure success by the revenue we add to your clinic.",
      es: "Medimos el éxito por los ingresos que sumamos a su clínica.",
    },
  },
];

export const FOUNDERS = [
  {
    name: "José Guadalupe",
    image:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzc21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MDQ0Njk1NXww&ixlib=rb-4.1.0&q=85",
    role: { en: "CEO & Director of Operations", es: "CEO y Director de Operaciones" },
    bio: {
      en: "José built MedPrime to give Puerto Rico's clinics the billing partner they deserved. He leads operations with an obsession for clean claims and faster collections.",
      es: "José fundó MedPrime para darles a las clínicas de Puerto Rico el socio de facturación que merecían. Dirige las operaciones con una obsesión por las reclamaciones limpias y los cobros más rápidos.",
    },
  },
  {
    name: "Edgar Ruiz",
    image:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzc21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MDQ0Njk1NXww&ixlib=rb-4.1.0&q=85",
    role: { en: "CFO & Financial Director", es: "CFO y Director Financiero" },
    bio: {
      en: "Edgar turns billing data into financial strategy. He ensures every clinic partner understands their numbers and watches their revenue grow month over month.",
      es: "Edgar convierte los datos de facturación en estrategia financiera. Se asegura de que cada clínica socia entienda sus cifras y vea crecer sus ingresos mes tras mes.",
    },
  },
];

export const COMPARISON = [
  {
    name: { en: "MedPrime", es: "MedPrime" },
    price: { en: "Transparent, performance-based", es: "Transparente, basado en resultados" },
    language: { en: "Fully bilingual (EN/ES)", es: "Totalmente bilingüe (ES/EN)" },
    local: { en: "Puerto Rico based", es: "Basado en Puerto Rico" },
    localYes: true,
    model: { en: "Dedicated local team", es: "Equipo local dedicado" },
    weakness: {
      en: "Boutique by design — we limit clients to stay personal",
      es: "Boutique por diseño: limitamos clientes para mantener el trato personal",
    },
    highlight: true,
  },
  {
    name: { en: "Kareo Billing", es: "Kareo Billing" },
    price: { en: "Per-claim + software fees", es: "Por reclamación + tarifas de software" },
    language: { en: "English-first", es: "Inglés primero" },
    local: { en: "Mainland US", es: "EE. UU. continental" },
    localYes: false,
    model: { en: "Software + add-on service", es: "Software + servicio adicional" },
    weakness: { en: "Generic support, no local context", es: "Soporte genérico, sin contexto local" },
  },
  {
    name: { en: "AdvancedMD", es: "AdvancedMD" },
    price: { en: "High monthly platform cost", es: "Alto costo mensual de plataforma" },
    language: { en: "English-first", es: "Inglés primero" },
    local: { en: "Mainland US", es: "EE. UU. continental" },
    localYes: false,
    model: { en: "Enterprise platform", es: "Plataforma empresarial" },
    weakness: { en: "Built for large groups, overkill for clinics", es: "Hecho para grupos grandes, excesivo para clínicas" },
  },
  {
    name: { en: "Outsource2India", es: "Outsource2India" },
    price: { en: "Low cost, hidden trade-offs", es: "Bajo costo, concesiones ocultas" },
    language: { en: "English only", es: "Solo inglés" },
    local: { en: "Offshore", es: "En el extranjero" },
    localYes: false,
    model: { en: "Volume offshore processing", es: "Procesamiento masivo en el extranjero" },
    weakness: { en: "Time zones, turnover, no local accountability", es: "Husos horarios, rotación de personal, sin responsabilidad local" },
  },
  {
    name: { en: "Local PR companies", es: "Empresas locales de PR" },
    price: { en: "Varies widely", es: "Varía ampliamente" },
    language: { en: "Spanish-first", es: "Español primero" },
    local: { en: "Puerto Rico", es: "Puerto Rico" },
    localYes: true,
    model: { en: "Small, manual operations", es: "Operaciones pequeñas y manuales" },
    weakness: { en: "Limited tech, inconsistent reporting", es: "Tecnología limitada, informes inconsistentes" },
  },
];

export const METRICS = [
  { label: { en: "Collections Rate", es: "Tasa de Cobro" }, value: 98 },
  { label: { en: "Denial Recovery", es: "Recuperación de Denegaciones" }, value: 89 },
  { label: { en: "Client Satisfaction", es: "Satisfacción del Cliente" }, value: 100 },
];

export const CLAIM_VOLUMES = {
  en: [
    "Under 100 claims / month",
    "100 – 500 claims / month",
    "500 – 1,000 claims / month",
    "1,000 – 5,000 claims / month",
    "5,000+ claims / month",
  ],
  es: [
    "Menos de 100 reclamaciones / mes",
    "100 – 500 reclamaciones / mes",
    "500 – 1,000 reclamaciones / mes",
    "1,000 – 5,000 reclamaciones / mes",
    "Más de 5,000 reclamaciones / mes",
  ],
};

export const CONTACT_INFO = {
  phone: "(787) 000-0000",
  email: "info@medprime.com",
  coverage: {
    en: "Serving Puerto Rico and all US states remotely.",
    es: "Atendemos Puerto Rico y todos los estados de EE. UU. de forma remota.",
  },
};

export const HIPAA_PRACTICES = [
  {
    icon: FileLock2,
    title: { en: "Business Associate Agreements", es: "Acuerdos de Asociado Comercial (BAA)" },
    desc: {
      en: "Every engagement begins with a signed BAA, legally binding us to protect your patients' protected health information (PHI) under HIPAA.",
      es: "Cada colaboración comienza con un BAA firmado, que nos obliga legalmente a proteger la información médica protegida (PHI) de sus pacientes bajo HIPAA.",
    },
  },
  {
    icon: Server,
    title: { en: "Data Security & Encryption", es: "Seguridad y Cifrado de Datos" },
    desc: {
      en: "PHI is encrypted in transit and at rest. We use access-controlled systems, audit logging, and secure infrastructure to keep records safe.",
      es: "La PHI se cifra en tránsito y en reposo. Usamos sistemas con control de acceso, registro de auditoría e infraestructura segura para mantener los registros a salvo.",
    },
  },
  {
    icon: GraduationCap,
    title: { en: "Staff Certification & Training", es: "Certificación y Capacitación del Personal" },
    desc: {
      en: "All team members complete HIPAA certification and ongoing privacy training. Access to PHI is strictly role-based and need-to-know.",
      es: "Todos los miembros del equipo completan la certificación HIPAA y capacitación continua en privacidad. El acceso a la PHI es estrictamente por función y según la necesidad de conocer.",
    },
  },
  {
    icon: Eye,
    title: { en: "Monitoring & Accountability", es: "Monitoreo y Responsabilidad" },
    desc: {
      en: "We continuously monitor access, maintain audit trails, and conduct regular reviews so every interaction with patient data is accountable.",
      es: "Monitoreamos continuamente los accesos, mantenemos registros de auditoría y realizamos revisiones periódicas para que cada interacción con los datos del paciente sea responsable.",
    },
  },
];

// ---------------- UI STRINGS ----------------
export const UI = {
  en: {
    getStarted: "Get Started",
    hero: {
      overline: "Medical Billing · Puerto Rico · Bilingual",
      titleA: "Your Clinic Deserves",
      accent: "Prime Revenue",
      body: "We turn the chaos of claims, denials, and follow-ups into clean, predictable revenue — so you can focus on patients while we get you paid.",
      ctaConsult: "Schedule Free Consultation",
      ctaServices: "Explore Services",
    },
    homeServices: {
      overline: "What We Do",
      titleA: "A Full Billing ",
      accent: "Department",
      titleB: ", Without the Overhead",
      viewAll: "View All Services",
    },
    testimonial: {
      quote:
        "MedPrime took our collections from a constant headache to something we never think about. Our revenue is up, our denials are down, and they speak our patients' language.",
      author: "Dr. Marisol Rivera — Family Practice, San Juan",
    },
    timeline: {
      overline: "The Process",
      titleA: "How It",
      accent: "Works",
      body: "Five deliberate steps from connection to collection — engineered for clean claims and faster payment.",
      step: "Step",
      cta: "Start the Process",
    },
    cta: {
      overline: "Ready When You Are",
      titleA: "Let's Talk About Your",
      accent: "Revenue",
      body: "Leave your email and we'll reach out to schedule your free consultation. No pressure, no jargon — just a clear plan for getting your clinic paid.",
      placeholder: "you@yourclinic.com",
      button: "Get Started",
      sending: "Sending…",
      success: "You're on the list. We'll be in touch shortly.",
      error: "Please enter a valid email address.",
    },
    footer: {
      brand:
        "Luxury-grade medical billing built for Puerto Rico's clinics. Bilingual, HIPAA-compliant, and obsessed with your revenue.",
      services: "Services",
      company: "Company",
      contact: "Contact",
      hipaa: "HIPAA Compliance",
      rights: "All rights reserved.",
      privacy: "Privacy Policy",
      hipaaNotice: "HIPAA Notice",
      terms: "Terms of Service",
      changeLang: "Change Language",
    },
    pages: {
      services: {
        overline: "Our Services",
        titleA: "Everything Your Clinic Needs to",
        accent: "Get Paid",
        subtitle:
          "Six interlocking services that function as your complete, outsourced billing department — precise, transparent, and built for Puerto Rico.",
        cta: "Schedule Free Consultation",
      },
      howItWorks: {
        overline: "The Process",
        titleA: "From Connection to",
        accent: "Collection",
        subtitle:
          "A deliberate, five-step process designed to keep claims clean, denials rare, and your revenue moving.",
      },
      about: {
        overline: "About MedPrime",
        titleA: "Built in Puerto Rico, for",
        accent: "Puerto Rico",
        subtitle:
          "We started MedPrime because the clinics on our island deserved a billing partner who understood them — their language, their payers, and their patients.",
        storyOverline: "Our Story",
        storyTitle: "Local Roots, Premium Standards",
        story: [
          "Too many clinic owners were losing real money to denied claims, slow collections, and billing partners who treated them like a ticket number. We knew the island deserved better.",
          "MedPrime was founded on a simple belief: world-class billing shouldn't require leaving Puerto Rico. We combine enterprise-grade technology and rigorous coding with the personal, bilingual service of a team that lives where you do.",
          "Today we operate as the dedicated billing department for clinics across the island and the mainland — measured by one thing: the revenue we add to your practice.",
        ],
        leadership: "Leadership",
        foundersTitle: "The Founders",
        teamTitle: "A Team of Certified Professionals",
        teamBody:
          "Behind every account is a team of certified medical billing and coding professionals — credentialed, continuously trained, and bilingual. They know payer rules cold, and they treat your revenue with the precision it deserves.",
        valuesOverline: "What We Stand For",
        valuesTitle: "Our Values",
      },
      whyUs: {
        overline: "Why MedPrime",
        titleA: "The Clear Choice for",
        accent: "PR Clinics",
        subtitle:
          "Compare us against the platforms and outsourcers — then see the numbers that define our work.",
        cols: {
          provider: "Provider",
          price: "Price",
          language: "Language",
          local: "Local Focus",
          model: "Service Model",
          weakness: "Weakness",
        },
        you: "You",
        metricsOverline: "By the Numbers",
        metricsTitle: "Performance That Speaks",
      },
      contact: {
        overline: "Get in Touch",
        titleA: "Let's Grow Your",
        accent: "Revenue",
        subtitle:
          "Tell us about your clinic and we'll show you exactly where your billing is leaking — and how we'll fix it.",
        talkTitle: "Talk to Us",
        talkBody: "Bilingual support, every step of the way.",
        addressLine: "Puerto Rico · Serving all US states remotely",
        formTitle: "Send a Message",
        ph: {
          name: "Name *",
          clinic: "Clinic Name",
          email: "Email *",
          phone: "(787) 000-0000",
          volume: "Monthly Claim Volume",
          message: "How can we help your clinic?",
        },
        send: "Send Message",
        sending: "Sending…",
        contactSuccess: "Message sent. Our team will reach out within one business day.",
        contactError: "Something went wrong. Please check your details and try again.",
        bookOverline: "Book a Slot",
        bookTitle: "Free 30-Minute Consultation",
        bookBody:
          "Pick a date and time that suits you. We'll confirm your booking by email and walk through a tailored plan for your clinic — no cost, no obligation.",
        calendar: "Calendar sync coming soon · We confirm every request personally",
        notes: "Anything we should know?",
        request: "Request Consultation",
        booking: "Booking…",
        phoneField: "Phone",
        bookingSuccess: "Consultation requested! We'll confirm your slot shortly.",
        bookingError: "Unable to book right now. Please try again.",
      },
      hipaa: {
        overline: "HIPAA Compliance",
        titleA: "Your Patients' Data Is",
        accent: "Protected",
        subtitle:
          "Trust is the foundation of billing. Here's exactly how we safeguard every record we touch.",
        intro:
          "MedPrime is fully committed to compliance with the Health Insurance Portability and Accountability Act (HIPAA). Protecting patient information isn't a checkbox for us — it's built into every process, every system, and every person on our team.",
        bottomTitle: "Peace of Mind, Guaranteed",
        bottomBody:
          "When you partner with MedPrime, you can reassure your patients that their information is handled with the highest standard of care and confidentiality — backed by signed agreements and a team that takes privacy as seriously as you do.",
      },
    },
    gate: {
      welcome: "Welcome to",
      choose: "Choose your language",
      sub: "Select how you'd like to experience MedPrime.",
      es: "Español",
      en: "English",
      esNote: "Continuar en Español",
      enNote: "Continue in English",
    },
  },
  es: {
    getStarted: "Comenzar",
    hero: {
      overline: "Facturación Médica · Puerto Rico · Bilingüe",
      titleA: "Su Clínica Merece",
      accent: "Ingresos de Primera",
      body: "Convertimos el caos de reclamaciones, denegaciones y seguimientos en ingresos limpios y predecibles, para que usted se enfoque en sus pacientes mientras nosotros nos encargamos de que le paguen.",
      ctaConsult: "Agende su Consulta Gratis",
      ctaServices: "Explorar Servicios",
    },
    homeServices: {
      overline: "Lo Que Hacemos",
      titleA: "Un Departamento de Facturación ",
      accent: "Completo",
      titleB: ", Sin el Costo Fijo",
      viewAll: "Ver Todos los Servicios",
    },
    testimonial: {
      quote:
        "MedPrime convirtió nuestros cobros de un dolor de cabeza constante en algo en lo que ya ni pensamos. Nuestros ingresos subieron, las denegaciones bajaron, y hablan el idioma de nuestros pacientes.",
      author: "Dra. Marisol Rivera — Medicina Familiar, San Juan",
    },
    timeline: {
      overline: "El Proceso",
      titleA: "Cómo",
      accent: "Funciona",
      body: "Cinco pasos deliberados desde la conexión hasta el cobro, diseñados para reclamaciones limpias y pagos más rápidos.",
      step: "Paso",
      cta: "Comenzar el Proceso",
    },
    cta: {
      overline: "Cuando Usted Esté Listo",
      titleA: "Hablemos de Sus",
      accent: "Ingresos",
      body: "Déjenos su correo y nos comunicaremos para agendar su consulta gratuita. Sin presión, sin tecnicismos, solo un plan claro para que su clínica cobre.",
      placeholder: "usted@suclinica.com",
      button: "Comenzar",
      sending: "Enviando…",
      success: "¡Listo! Está en nuestra lista. Nos comunicaremos en breve.",
      error: "Por favor, ingrese un correo electrónico válido.",
    },
    footer: {
      brand:
        "Facturación médica de grado lujo, hecha para las clínicas de Puerto Rico. Bilingüe, en cumplimiento con HIPAA y obsesionada con sus ingresos.",
      services: "Servicios",
      company: "Empresa",
      contact: "Contacto",
      hipaa: "Cumplimiento HIPAA",
      rights: "Todos los derechos reservados.",
      privacy: "Política de Privacidad",
      hipaaNotice: "Aviso HIPAA",
      terms: "Términos de Servicio",
      changeLang: "Cambiar idioma",
    },
    pages: {
      services: {
        overline: "Nuestros Servicios",
        titleA: "Todo lo Que Su Clínica Necesita para",
        accent: "Cobrar",
        subtitle:
          "Seis servicios entrelazados que funcionan como su departamento de facturación completo y tercerizado: preciso, transparente y diseñado para Puerto Rico.",
        cta: "Agende su Consulta Gratis",
      },
      howItWorks: {
        overline: "El Proceso",
        titleA: "De la Conexión al",
        accent: "Cobro",
        subtitle:
          "Un proceso deliberado de cinco pasos, diseñado para mantener las reclamaciones limpias, las denegaciones escasas y sus ingresos en movimiento.",
      },
      about: {
        overline: "Sobre MedPrime",
        titleA: "Creado en Puerto Rico, para",
        accent: "Puerto Rico",
        subtitle:
          "Creamos MedPrime porque las clínicas de nuestra isla merecían un socio de facturación que las entendiera: su idioma, sus aseguradoras y sus pacientes.",
        storyOverline: "Nuestra Historia",
        storyTitle: "Raíces Locales, Estándares de Primera",
        story: [
          "Demasiados dueños de clínicas perdían dinero real por reclamaciones denegadas, cobros lentos y socios de facturación que los trataban como un número de ticket. Sabíamos que la isla merecía algo mejor.",
          "MedPrime nació de una convicción simple: la facturación de clase mundial no debería exigir salir de Puerto Rico. Combinamos tecnología de nivel empresarial y una codificación rigurosa con el servicio personal y bilingüe de un equipo que vive donde usted vive.",
          "Hoy operamos como el departamento de facturación dedicado de clínicas en toda la isla y el continente, medidos por una sola cosa: los ingresos que sumamos a su práctica.",
        ],
        leadership: "Liderazgo",
        foundersTitle: "Los Fundadores",
        teamTitle: "Un Equipo de Profesionales Certificados",
        teamBody:
          "Detrás de cada cuenta hay un equipo de profesionales certificados en facturación y codificación médica: acreditados, en formación continua y bilingües. Conocen al detalle las reglas de las aseguradoras y tratan sus ingresos con la precisión que merecen.",
        valuesOverline: "Lo Que Nos Define",
        valuesTitle: "Nuestros Valores",
      },
      whyUs: {
        overline: "Por Qué MedPrime",
        titleA: "La Opción Clara para las",
        accent: "Clínicas de PR",
        subtitle:
          "Compárenos con las plataformas y los tercerizadores, y luego vea las cifras que definen nuestro trabajo.",
        cols: {
          provider: "Proveedor",
          price: "Precio",
          language: "Idioma",
          local: "Enfoque Local",
          model: "Modelo de Servicio",
          weakness: "Debilidad",
        },
        you: "Usted",
        metricsOverline: "En Cifras",
        metricsTitle: "Resultados Que Hablan",
      },
      contact: {
        overline: "Contáctenos",
        titleA: "Hagamos Crecer Sus",
        accent: "Ingresos",
        subtitle:
          "Cuéntenos sobre su clínica y le mostraremos exactamente dónde se está filtrando su facturación, y cómo lo resolveremos.",
        talkTitle: "Hablemos",
        talkBody: "Soporte bilingüe en cada paso.",
        addressLine: "Puerto Rico · Atendemos todos los estados de EE. UU. de forma remota",
        formTitle: "Envíe un Mensaje",
        ph: {
          name: "Nombre *",
          clinic: "Nombre de la Clínica",
          email: "Correo Electrónico *",
          phone: "(787) 000-0000",
          volume: "Volumen Mensual de Reclamaciones",
          message: "¿Cómo podemos ayudar a su clínica?",
        },
        send: "Enviar Mensaje",
        sending: "Enviando…",
        contactSuccess: "Mensaje enviado. Nuestro equipo se comunicará en un día hábil.",
        contactError: "Algo salió mal. Verifique sus datos e inténtelo de nuevo.",
        bookOverline: "Reserve un Espacio",
        bookTitle: "Consulta Gratis de 30 Minutos",
        bookBody:
          "Elija una fecha y hora que le convenga. Confirmaremos su reserva por correo y repasaremos un plan a la medida de su clínica, sin costo ni compromiso.",
        calendar: "Sincronización de calendario próximamente · Confirmamos cada solicitud personalmente",
        notes: "¿Algo que debamos saber?",
        request: "Solicitar Consulta",
        booking: "Reservando…",
        phoneField: "Teléfono",
        bookingSuccess: "¡Consulta solicitada! Confirmaremos su espacio en breve.",
        bookingError: "No se pudo reservar en este momento. Inténtelo de nuevo.",
      },
      hipaa: {
        overline: "Cumplimiento HIPAA",
        titleA: "Los Datos de Sus Pacientes Están",
        accent: "Protegidos",
        subtitle:
          "La confianza es la base de la facturación. Así es exactamente como protegemos cada registro que manejamos.",
        intro:
          "MedPrime está plenamente comprometido con el cumplimiento de la Ley de Portabilidad y Responsabilidad del Seguro Médico (HIPAA). Proteger la información del paciente no es un trámite para nosotros: está integrado en cada proceso, cada sistema y cada persona de nuestro equipo.",
        bottomTitle: "Tranquilidad Garantizada",
        bottomBody:
          "Cuando se asocia con MedPrime, puede asegurarles a sus pacientes que su información se maneja con el más alto estándar de cuidado y confidencialidad, respaldado por acuerdos firmados y un equipo que toma la privacidad tan en serio como usted.",
      },
    },
    gate: {
      welcome: "Bienvenido a",
      choose: "Elija su idioma",
      sub: "Seleccione cómo desea explorar MedPrime.",
      es: "Español",
      en: "English",
      esNote: "Continuar en Español",
      enNote: "Continue in English",
    },
  },
};
